'use client';

import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { RefreshCw, X } from 'lucide-react';

import { cn } from '../lib/utils';
import { Trans } from './trans';

/**
 * Current version of the app that is running
 */
let version: string | null = null;

/**
 * Default interval time in seconds to check for new version
 * By default, it is set to 60 seconds
 */
const DEFAULT_REFETCH_INTERVAL = 60;

/**
 * Auto-dismiss timeout in milliseconds (10 seconds)
 */
const AUTO_DISMISS_TIMEOUT = 10000;

/**
 * Default interval time in seconds to check for new version
 */
const VERSION_UPDATER_REFETCH_INTERVAL_SECONDS =
  process.env.NEXT_PUBLIC_VERSION_UPDATER_REFETCH_INTERVAL_SECONDS;

export function VersionUpdater(props: { intervalTimeInSecond?: number }) {
  const { data } = useVersionUpdater(props);
  const [dismissed, setDismissed] = useState(false);
  const [autoDismissedVersion, setAutoDismissedVersion] = useState<
    string | null
  >(null);

  // Derive visibility: show if version changed, not manually dismissed,
  // and not already auto-dismissed for this specific version
  const shouldShow =
    data?.didChange &&
    !dismissed &&
    data.currentVersion !== autoDismissedVersion;

  // Auto-dismiss timer - only fires async callback
  useEffect(() => {
    if (!shouldShow || !data?.currentVersion) {
      return;
    }

    const currentVersion = data.currentVersion;
    const timer = setTimeout(() => {
      setAutoDismissedVersion(currentVersion);
    }, AUTO_DISMISS_TIMEOUT);

    return () => clearTimeout(timer);
  }, [shouldShow, data?.currentVersion]);

  const handleReload = () => {
    window.location.reload();
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissed(true);
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={handleReload}
      className={cn(
        // Über den festen Leisten unten und über dem AGB-Banner (der auf
        // `--app-unterkante` + 1rem steht) — hier stand fest `bottom-20`.
        'fixed right-4 bottom-[calc(var(--app-unterkante,0px)+5rem)] z-50',
        'flex items-center gap-2',
        // a2-schwebend: Schwebender Hinweis unten rechts — liegt ueber dem Inhalt, nicht darin.
        'bg-card rounded-lg border px-3 py-2 shadow-lg',
        'cursor-pointer transition-all duration-200',
        // a2-schwebend: Derselbe schwebende Hinweis, angehobener Zustand beim Zeigen.
        'hover:bg-accent hover:shadow-xl',
        'animate-in slide-in-from-bottom-2 fade-in',
      )}
    >
      <RefreshCw className="text-muted-foreground h-4 w-4" />

      <span className="text-sm font-medium">
        <Trans i18nKey="common:updateAvailable" />
      </span>

      <button
        onClick={handleDismiss}
        className="hover:bg-muted ml-1 rounded p-0.5"
        aria-label="Dismiss"
      >
        <X className="text-muted-foreground h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function useVersionUpdater(props: { intervalTimeInSecond?: number } = {}) {
  const interval = VERSION_UPDATER_REFETCH_INTERVAL_SECONDS
    ? Number(VERSION_UPDATER_REFETCH_INTERVAL_SECONDS)
    : DEFAULT_REFETCH_INTERVAL;

  const refetchInterval = (props.intervalTimeInSecond ?? interval) * 1000;

  // start fetching new version after half of the interval time
  const staleTime = refetchInterval / 2;

  return useQuery({
    queryKey: ['version-updater'],
    staleTime,
    gcTime: refetchInterval,
    refetchIntervalInBackground: true,
    refetchInterval,
    initialData: null,
    queryFn: async () => {
      const url = new URL('/api/version', process.env.NEXT_PUBLIC_SITE_URL);
      const response = await fetch(url.toString());

      const currentVersion = await response.text();
      const oldVersion = version;

      version = currentVersion;

      const didChange = oldVersion !== null && currentVersion !== oldVersion;

      return {
        currentVersion,
        oldVersion,
        didChange,
      };
    },
  });
}
